import React, { useMemo } from "react";
import { useTheme, Theme } from '../contexts/ThemeContext';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  container: {
    maxWidth: '672px', // 2xl equivalent (42rem)
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: theme.spacing.xl,
    color: theme.colors.text,
  },
  title: {
    fontSize: theme.typography.sizes.xxxl, // 3xl equivalent
    fontWeight: theme.typography.weights.bold,
    marginBottom: theme.spacing.lg,
    color: theme.colors.text,
  },
  effectiveDate: {
    marginBottom: theme.spacing.sm,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
    color: theme.colors.text,
  },
  paragraph: {
    marginBottom: theme.spacing.sm,
    lineHeight: theme.typography.lineHeights.relaxed,
    color: theme.colors.text,
  },
  list: {
    marginLeft: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
    listStyleType: 'disc',
    color: theme.colors.text,
  },
  listItem: {
    marginBottom: theme.spacing.xs,
    lineHeight: theme.typography.lineHeights.relaxed,
  },
  link: {
    color: theme.colors.primary,
    textDecoration: 'underline',
  },
  strong: {
    fontWeight: theme.typography.weights.semiBold,
  },
  separator: {
    marginVertical: theme.spacing.xl,
    borderTopWidth: 1,
    borderTopStyle: 'solid' as const,
    borderTopColor: theme.colors.border,
  },
  footer: {
    fontStyle: 'italic',
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  contactParagraph: {
    marginBottom: theme.spacing.xl,
    color: theme.colors.text,
  },
});

const TermsOfUse: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Terms of Use (EULA)</h1>
      <p style={styles.effectiveDate}>Effective Date: September 21, 2025</p>
      
      <h2 style={styles.sectionTitle}>Agreement to Terms</h2>
      <p style={styles.paragraph}>
        These Terms of Use ("Terms") govern your use of ChordBaker, including both the website at{' '}
        <a 
          href="https://chordbaker.com/" 
          style={styles.link}
        >
          https://chordbaker.com/
        </a>{' '}
        and the mobile application ("App"). By accessing or using ChordBaker, you agree to be bound by these Terms.
      </p>
      
      <h2 style={styles.sectionTitle}>Description of Service</h2>
      <p style={styles.paragraph}>ChordBaker is a chord chart and songbook application that allows users to:</p>
      <ul style={styles.list}>
        <li style={styles.listItem}>Create and edit chord charts and lyrics</li>
        <li style={styles.listItem}>Organize songs into collections</li>
        <li style={styles.listItem}>Export and share musical content</li>
        <li style={styles.listItem}>Access guitar chord diagrams and musical tools</li>
      </ul>
      
      <h2 style={styles.sectionTitle}>User Content and Intellectual Property</h2>
      <p style={styles.paragraph}>
        <span style={styles.strong}>Your Content:</span> You retain ownership of any original content you create using ChordBaker. You are responsible for ensuring you have the right to use any copyrighted material you input into the application.
      </p>
      <p style={styles.paragraph}>
        <span style={styles.strong}>Copyright Compliance:</span> You agree not to use ChordBaker to infringe on copyrighted musical works without proper authorization. You are solely responsible for compliance with copyright laws.
      </p>
      <p style={styles.paragraph}>
        <span style={styles.strong}>Our Content:</span> ChordBaker's interface, chord diagrams, and original features are protected by intellectual property laws.
      </p>
      
      <h2 style={styles.sectionTitle}>Acceptable Use</h2>
      <p style={styles.paragraph}>You agree to use ChordBaker only for lawful purposes and in accordance with these Terms. You agree not to:</p>
      <ul style={styles.list}>
        <li style={styles.listItem}>Use the service to infringe on intellectual property rights</li>
        <li style={styles.listItem}>Attempt to reverse engineer, hack, or compromise the application</li>
        <li style={styles.listItem}>Upload malicious content or attempt to harm other users</li>
        <li style={styles.listItem}>Use the service for any commercial purpose without authorization</li>
        <li style={styles.listItem}>Violate any applicable laws or regulations</li>
      </ul>
      
      <h2 style={styles.sectionTitle}>Privacy and Data</h2>
      <p style={styles.paragraph}>
        Your privacy is important to us. Please review our{' '}
        <a 
          href="https://chordbaker.com/privacy" 
          style={styles.link}
        >
          Privacy Policy
        </a>{' '}
        to understand how we handle information. ChordBaker is designed to minimize data collection and protect user privacy.
      </p>
      
      <h2 style={styles.sectionTitle}>Service Availability</h2>
      <p style={styles.paragraph}>
        We strive to maintain ChordBaker's availability, but we cannot guarantee uninterrupted service. We reserve the right to modify, suspend, or discontinue any aspect of the service with or without notice.
      </p>
      
      <h2 style={styles.sectionTitle}>Disclaimers and Limitation of Liability</h2>
      <p style={styles.paragraph}>
        ChordBaker is provided "as is" without warranties of any kind. We are not liable for any damages arising from your use of the service, including but not limited to data loss, copyright infringement claims, or service interruptions.
      </p>
      
      <h2 style={styles.sectionTitle}>Indemnification</h2>
      <p style={styles.paragraph}>
        You agree to indemnify and hold harmless ChordBaker and its developers from any claims arising from your use of the service, including copyright infringement claims related to content you input or create.
      </p>
      
      <h2 style={styles.sectionTitle}>Termination</h2>
      <p style={styles.paragraph}>
        We may terminate or suspend your access to ChordBaker at any time, with or without cause. You may stop using the service at any time.
      </p>
      
      <h2 style={styles.sectionTitle}>Governing Law</h2>
      <p style={styles.paragraph}>
        These Terms are governed by the laws of your jurisdiction. Any disputes will be resolved in accordance with applicable local laws.
      </p>
      
      <h2 style={styles.sectionTitle}>Changes to Terms</h2>
      <p style={styles.paragraph}>
        We may update these Terms from time to time. Continued use of ChordBaker after changes constitutes acceptance of the new Terms. We will notify users of significant changes through the application or website.
      </p>
      
      <h2 style={styles.sectionTitle}>Contact Information</h2>
      <p style={styles.paragraph}>If you have any questions about these Terms of Use, please contact us at:</p>
      <p style={styles.contactParagraph}>tomas@chordbaker.com</p>
      
      <div style={styles.separator} />
      
      <p style={styles.footer}>
        By using ChordBaker, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
      </p>
    </div>
  );
};

export default TermsOfUse;